import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ADMIN_ROUTES, CERTIFIRMA } from '../../core/constants/nomenclature.constants';

export type AdminKind = 'token-virtual' | 'token-virtual-codigo' | 'p10';
export type AdminTab = 'descargar' | 'contrasena' | 'unir-llaves';

interface AdminTabItem {
  id: AdminTab;
  label: string;
  desc: string;
  icon: string;
}

@Component({
  selector: 'app-admin-hub',
  templateUrl: './admin-hub.component.html',
  styleUrls: ['./admin-hub.component.css'],
  standalone: false
})
export class AdminHubComponent implements OnInit, OnDestroy {
  adminKind: AdminKind = 'token-virtual';
  activeTab: AdminTab = 'descargar';
  supportsReset = true;
  supportsJoinKeys = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.syncFromRoute();
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.syncFromRoute());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get pageTitle(): string {
    switch (this.adminKind) {
      case 'token-virtual-codigo':
        return `Gestionar ${CERTIFIRMA.VIRTUAL_OTP}`;
      case 'p10':
        return `Gestionar ${CERTIFIRMA.PKCS10}`;
      default:
        return `Gestionar ${CERTIFIRMA.VIRTUAL}`;
    }
  }

  get pageSubtitle(): string {
    switch (this.adminKind) {
      case 'token-virtual-codigo':
        return 'Descarga tu certificado o recupera tu clave con el código de seguridad enviado a tu correo.';
      case 'p10':
        return 'Descarga tu certificado o une las llaves para generar el archivo PFX.';
      default:
        return 'Revisa tus certificados activos en la nube y actualiza tus claves de acceso.';
    }
  }

  get tabs(): AdminTabItem[] {
    const download: AdminTabItem = {
      id: 'descargar',
      label: 'Descargar certificado',
      desc: 'Obtener tu certificado',
      icon: 'fas fa-download'
    };
    const joinKeys: AdminTabItem = {
      id: 'unir-llaves',
      label: 'Unir llaves o PFX',
      desc: 'Generar archivo .pfx',
      icon: 'fas fa-link'
    };
    if (this.supportsJoinKeys && !this.supportsReset) {
      return [download, joinKeys];
    }
    if (!this.supportsReset) {
      return [download];
    }
    return [
      download,
      {
        id: 'contrasena',
        label: 'Recuperar clave Virtual',
        desc: 'Restablecer acceso',
        icon: 'fas fa-key'
      }
    ];
  }

  selectTab(tab: AdminTab): void {
    if (tab === 'contrasena' && !this.supportsReset) {
      return;
    }
    if (tab === 'unir-llaves' && !this.supportsJoinKeys) {
      return;
    }
    const base = this.basePath;
    this.router.navigate([base, tab]);
  }

  private get basePath(): string {
    switch (this.adminKind) {
      case 'token-virtual-codigo':
        return ADMIN_ROUTES.VIRTUAL_OTP;
      case 'p10':
        return ADMIN_ROUTES.PKCS10;
      default:
        return ADMIN_ROUTES.VIRTUAL;
    }
  }

  private syncFromRoute(): void {
    const data = this.route.snapshot.data;
    this.adminKind = (data['adminKind'] as AdminKind) || 'token-virtual';
    this.supportsReset = data['supportsReset'] !== false;
    this.supportsJoinKeys = data['supportsJoinKeys'] === true;

    const child = this.route.firstChild?.snapshot.url[0]?.path;
    if (child === 'contrasena' && this.supportsReset) {
      this.activeTab = 'contrasena';
    } else if (child === 'unir-llaves' && this.supportsJoinKeys) {
      this.activeTab = 'unir-llaves';
    } else {
      this.activeTab = 'descargar';
    }
  }
}
